/* eslint-disable react/jsx-handler-names */
import { stop } from '#~/control/calibration/commands/stop.js';
import { wake } from '#~/device/commands/wake.js';
import { post } from '#~/notifications/utils.js';
import { MessageIds } from '#~/routes/room/watchdog/constants.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import { BadgeStatus } from './types.js';
import type { Props } from './types.js';
import { Badge as _Badge } from '@studio/design-system/badge';
import type { Severity } from '@studio/design-system/messaging';
import type { Device } from '@studio/models/device';
import type { T_Maybe, T_Nullable } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { forwardRef, useEffect, useState } from 'react';
import { twMerge } from 'cn';

const passthruMap: Record<
  number,
  {
    actionSurface?: 'pill' | 'icon';
    icon?: string;
    message: string;
    onClick?: (deviceId: Device['id']) => void;
    severity: Severity;
    ttl: number;
  }
> = {
  [BadgeStatus.BADGE_STATUS_CONNECTING]: {
    message: 'Connecting...',
    severity: 'info' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_CONNECTED]: {
    message: 'Connected',
    severity: 'success' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_MOVEMENT_CONNECTING]: {
    message: 'Controls Connecting...',
    severity: 'info' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_VIDEO_CONNECTING]: {
    message: 'Video Connecting...',
    severity: 'info' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_AUDIO_CONNECTING]: {
    message: 'Audio Connecting...',
    severity: 'info' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_MOVEMENT_CONNECTION_FAILED]: {
    message: 'Controls Connection Failed',
    severity: 'error' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_VIDEO_CONNECTION_FAILED]: {
    message: 'Video Connection Failed',
    severity: 'error' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_AUDIO_CONNECTION_FAILED]: {
    message: 'Audio Connection Failed',
    severity: 'error' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_REBOOTING]: {
    message: 'Rebooting...',
    severity: 'warning' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_BOOTING]: {
    message: 'Booting...',
    severity: 'warning' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_SHUTTING_DOWN]: {
    message: 'Shutting Down...',
    severity: 'warning' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_IDLE]: {
    message: 'Wake Up',
    severity: 'warning' as Severity,
    ttl: -1,
    icon: 'power',
    onClick: async (deviceId: Device['id']) => {
      await wake(deviceId);
    }
  },
  [BadgeStatus.BADGE_STATUS_CALIBRATING]: {
    // Per D-B8 (ENG-4719): stopping calibration is destructive, so only the ✕ icon
    // may trigger it — not the whole pill. The 'icon' surface makes the icon the
    // sole button and leaves the "Calibrating…" label inert.
    actionSurface: 'icon',
    message: 'Calibrating...',
    severity: 'info' as Severity,
    ttl: -1,
    icon: 'close',
    onClick: async (deviceId: Device['id']) => {
      post({
        id: [MessageIds.calibration, deviceId].join('.'),
        message: 'The device calibration process will stop shortly.',
        title: 'Calibration Stopping',
        deviceId
      });

      await stop(deviceId);
    }
  },
  [BadgeStatus.BADGE_STATUS_CALIBRATION_GENERIC]: {
    message: 'Calibration Updated',
    severity: 'info' as Severity,
    ttl: 10000
  },
  [BadgeStatus.BADGE_STATUS_CALIBRATION_FAILED]: {
    message: 'Calibration Failed',
    severity: 'error' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_CALIBRATION_SUCCEEDED]: {
    message: 'Calibration Succeeded',
    severity: 'success' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_BRIDGE_OFFLINE]: {
    message: 'Bridge Offline',
    severity: 'error' as Severity,
    icon: 'bridge',
    ttl: -1
  },
  // ENG-3716: cause-keyed terminal states for a video source whose track never
  // subscribed (livekit/track-wait.ts) — actionable copy instead of an
  // unbounded "Connecting...".
  [BadgeStatus.BADGE_STATUS_VIDEO_CAMERA_UNREACHABLE]: {
    message: 'Camera Unreachable - Check Camera & Network',
    severity: 'error' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_VIDEO_STREAM_INTERRUPTED]: {
    message: 'Stream Interrupted - Reconnect the Source',
    severity: 'error' as Severity,
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_VIDEO_FORMAT_UNSUPPORTED]: {
    message: 'Video Format Unsupported - Set Camera to H.264 and Reconnect',
    severity: 'error',
    ttl: -1
  },
  [BadgeStatus.BADGE_STATUS_VIDEO_DEVICE_NOT_DISCOVERED]: {
    message: 'Previous Camera Not Found - Reselect It',
    severity: 'error' as Severity,
    ttl: -1
  },
  // Terminal like "Video Connection Failed": the agent cannot stream this feed until it changes.
  [BadgeStatus.BADGE_STATUS_VIDEO_H265_UNSUPPORTED]: {
    message: 'H.265 is not supported',
    severity: 'error' as Severity,
    ttl: -1
  }
};

/** Video `<Badge>` component. */
export const Badge = forwardRef<HTMLDivElement, Props>(
  (
    { badgeStatus, calibrationProgress, className, deviceId, disabled, wrap, ...props },
    forwardedRef
  ): T_Nullable<ReactElement> => {
    const [passthru, setPassthru] = useState<{
      actionSurface?: 'pill' | 'icon';
      icon?: string;
      message: string;
      onClick?: (deviceId: Device['id']) => void;
      severity: Severity;
    }>();

    const [timer, setTimer] = useState<T_Maybe<NodeJS.Timeout>>();

    useEffect(() => {
      if (badgeStatus === undefined) {
        setPassthru(undefined);
        clearTimeout(timer);

        return;
      }

      const nextPassthru = passthruMap[Number(badgeStatus)];

      setPassthru(nextPassthru);

      clearTimeout(timer);

      if (nextPassthru === undefined || nextPassthru.ttl === -1) {
        return;
      }

      setTimer(
        setTimeout(() => {
          setPassthru(undefined);
        }, nextPassthru.ttl)
      );
      // eslint-disable-next-line react-hooks-configurable/exhaustive-deps
    }, [badgeStatus, setPassthru, setTimer]);

    let message = passthru ? passthru.message : '';

    if (passthru && calibrationProgress && badgeStatus === BadgeStatus.BADGE_STATUS_CALIBRATING) {
      message = `Calibrating (${calibrationProgress}%)`;
    }

    if (!passthru) {
      return null;
    }

    const badge = (
      <_Badge
        ref={forwardedRef}
        {...props}
        // Per rule: playwright-stag-selectors-only (platform) — expose each source's failure status.
        {...DataSTag(Sel.MainRoomVideoBadge, 'status', deviceId)}
        actionSurface={passthru.actionSurface}
        className={twMerge(
          // Action pills keep one fixed-height line: their icon divider is h-full of the pill.
          wrap && !passthru.icon && 'h-auto min-h-28 whitespace-normal py-4',
          className
        )}
        // Gate the badge's action while its (e.g. configuration) action is in flight.
        disabled={disabled}
        icon={passthru.icon}
        message={message}
        onClick={
          passthru.onClick
            ? (event): void => {
                // Per D-B8 (ENG-4719): an actionable pill does exactly one thing and
                // never also selects the room tile it overlays, so stop the click from
                // bubbling to the tile's selection handler.
                event.stopPropagation();
                passthru.onClick?.(deviceId);
              }
            : undefined
        }
        severity={passthru.severity}
      />
    );

    // In the 'icon' surface only the ✕ is a button; the "Calibrating…" label has no
    // handler and would still bubble a click to the tile. Wrap it in a display:contents
    // catcher (no layout box of its own) so a click anywhere on the pill stays off the
    // tile, while calibration only stops from the ✕ above. Per D-B8 (ENG-4719).
    return passthru.onClick && passthru.actionSurface === 'icon' ? (
      <div className='contents' onClick={(event): void => event.stopPropagation()}>
        {badge}
      </div>
    ) : (
      badge
    );
  }
);
